import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { Fill, Stroke, Style } from 'ol/style';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { fhr } from '../utils/fhr.js';
import { config } from 'config';
import { dataSeries } from '../dataSeries.js';
import { Text } from 'ol/style.js';

/**
 * Creates and places labels on the map.
 * @module layers/label
 * @type {Object}
 */
export const label = {

  /**
   * @type {JsonStatReader}
   */
  _reader: null,

  /**
   * The layer containing the label points
   * @type {VectorLayer}
   */
  layer: null,

  /**
   * The label points fetched from the labelPointService
   * @type {GeoJSON|null}
   */
  labelPoints: null,

  /**
   * URL of the service
   * @type {String}
   * @private
   */
  _url: config.service.geo + '/geometry/labelpoint',

  /**
   * Initializes the label layer.
   * @returns {label}
   * @param {JsonStatReader} jsonStatReader
   * @param {DataSeries} rowData
   */
  init: async function (jsonStatReader, rowData) {
    const varNr = jsonStatReader.getRegionId();
    this._reader = jsonStatReader;
    dataSeries.init(jsonStatReader);
    this.labelPoints = await this._fetchLabelpoints(varNr);
    this.layer = this._get(this.labelPoints, rowData);

    return this;
  },

  /**
   * Create and return the label layer.
   * @param {GeoJSON} labelPoints
   * @param {DataSeries} rowData
   * @return {VectorLayer}
   * @private
   */
  _get (labelPoints, rowData) {
    return new VectorLayer({
      source: new VectorSource({
        features: this._getFeatures(labelPoints)
      }),
      zIndex: config.layerZIndex.label,
      style: feature => this._getStyle(feature, rowData)
    });
  },

  /**
   * Return the label points as map features.
   * @param {GeoJSON} labelPoints
   * @return {Feature[]}
   * @private
   */
  _getFeatures (labelPoints) {
    const features = [];

    labelPoints.features.forEach(region => {
      const regionId = region.properties.regionid;
      const pointFeature = new Feature({
        geometry: new Point(region.geometry.coordinates)
      });
      pointFeature.setId(regionId);  // observable property
      features.push(pointFeature);
    });

    return features;
  },

  /**
   * Update the labels on the layer.
   * @returns {void}
   * @param {DataSeries} rowData
   */
  update (rowData) {
    if (this.layer !== null) {
      this.layer.setStyle(feature => {
        return this._getStyle(feature, rowData);
      });
    }
  },

  /**
   * Fetch the label points.
   * @returns {Promise<GeoJSON>}
   * @private
   * @param {String} varNr
   */
  _fetchLabelpoints (varNr) {
    return fetch(this._url + '/' + varNr).then(fhr.json);
  },

  /**
   * Create and return the label style.
   * @param {Feature} feature
   * @param {DataSeries} rowData
   * @return {Style|false}
   * @private
   */
  _getStyle (feature, rowData) {
    const item = rowData.series.find(region => Number(region.id) === feature.getId());
    if (item === undefined) {
      return false;
    }
    return new Style({
      text: new Text({
        text: item.label + '\n' + dataSeries.createStr(item, false),
        overflow: true,
        scale: this._calcFontScale(),
        font: '12px "Open Sans", sans-serif',
        placement: 'point',
        fill: new Fill({
          color: '#000'
        }),
        stroke: new Stroke({
          color: '#fff',
          width: 2
        })
      })
    });
  },

  /**
   * Calculate the font scaling factor.
   * Calculates the scaling factor based on the current view extent relative to the initial extent,
   * adjusted for the map's viewport size (screen dimensions).
   * @return {Number}
   * @private
   */
  _calcFontScale () {
    const map = this.layer.getMapInternal();
    const view = map.getView();
    const initialExtent = view.get('initialExtent');
    const initialResolution = view.get('initialResolution');
    const currentResolution = view.getResolution();
    const mapSize = map.getSize();

    // Part 1: Calculate an initial scale factor that considers both
    // the geographical extent and the screen size.

    const geoWidth = initialExtent[2] - initialExtent[0];
    const pixelWidth = mapSize[0];

    // --- Reference values for normalization ---
    // A reference geographical width (e.g., width of Switzerland in map units).
    // Your projection EPSG:3857 uses meters. Adjust if needed.
    const refGeoWidth = 300000; // e.g., 300km
    // A reference screen pixel width (e.g., a typical desktop browser width).
    const refPixelWidth = 800;
    // A base scale factor for the reference setup. Tune this for the overall size.
    const refBaseScale = 1;

    // Calculate the initial scale.
    // It's proportional to the sqrt of the geo width and pixel width.
    const geoScale = Math.sqrt(geoWidth / refGeoWidth);
    const pixelScale = Math.sqrt(pixelWidth / refPixelWidth);
    const initialScale = refBaseScale * geoScale * pixelScale;

    // Part 2: Calculate the zoom factor.
    const zoomScale = Math.sqrt(initialResolution / currentResolution);

    // The final scale is the combination of the initial scale and the zoom scale.
    // On first load, zoomScale is 1.
    return initialScale * zoomScale;
  }

};